// ===============================================================
// 📞 CallListener.js — FINAL (NORMAL + MATCH CALLS) — FINAL FIXED
// - normal call   => acceptCall / rejectCall
// - matched call  => acceptMatchedCall / rejectMatchedCall
// - UI moved to IncomingCallCard
// - FIX 1: استقبال المكالمات التالية بدون Refresh
// - FIX 2: resolve avatar (PATH/gs:///downloadURL -> http)
// ===============================================================

import React, { useEffect, useState, useContext, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../context/AuthContext";
import { db, storage } from "../firebase";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  doc,
  getDoc,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getDownloadURL, ref as storageRef } from "firebase/storage";
import IncomingCallCard from "./IncomingCallCard";
import useLivoraToast from "../hooks/useLivoraToast";
import { callDebugLog } from "./callDebug";
import { resolveCallRoomRoute } from "./callRoute";
import {
  buildReturnContextFromLocation,
  saveReturnContext,
} from "./nativeCallReturnContext";
import { logCallStartupEvent, clearCallStartupTrace } from "./callStartupTrace";
import {
  beginNativeCallPreparation,
  clearNativeCallPreparation,
} from "./nativeCallPreparation";

// ===============================================================
// ✅ Image helpers (PATH/gs:///downloadURL -> http)
// ===============================================================
function isHttpUrl(v) {
  return typeof v === "string" && /^https?:\/\//i.test(v);
}

function storagePathFromValue(value) {
  if (!value || typeof value !== "string") return "";

  // already a path (profileImages/..)
  if (!isHttpUrl(value) && !value.startsWith("gs://")) return value;

  // gs:// keep as is
  if (value.startsWith("gs://")) return value;

  // firebase downloadURL -> extract path
  try {
    const part = value.split("/o/")[1];
    if (!part) return "";
    return decodeURIComponent((part.split("?")[0] || "").trim());
  } catch {
    return "";
  }
}

async function resolveToDownloadUrl(value) {
  if (!value) return "";

  // keep normal http links (non firebase)
  if (
    isHttpUrl(value) &&
    !value.includes("firebasestorage.googleapis.com")
  ) {
    return value;
  }

  // if it's already a firebase downloadURL, keep it
  if (
    isHttpUrl(value) &&
    value.includes("firebasestorage.googleapis.com")
  ) {
    return value;
  }

  try {
    const pathOrGs = storagePathFromValue(value) || value;
    return await getDownloadURL(storageRef(storage, pathOrGs));
  } catch (e) {
    console.warn("CallListener resolveToDownloadUrl failed:", value, e);
    return "";
  }
}

const HEARTBEAT_BUSY_MAX_MS = 75 * 1000;
const MISSING_HEARTBEAT_GRACE_MS = 3 * 60 * 1000;

function isValidTimestamp(value) {
  return Boolean(value && typeof value.toMillis === "function");
}

function getFallbackAgeAnchor(call) {
  const candidates = [
    call?.startedAt,
    call?.acceptedAt,
    call?.updatedAt,
    call?.createdAt,
  ];

  for (const candidate of candidates) {
    if (isValidTimestamp(candidate)) {
      return candidate;
    }
  }

  return null;
}

function evaluateOngoingBusy(call, nowMs) {
  if (String(call?.status || "").toLowerCase() !== "ongoing") {
    return {busy: false, reason: "not_ongoing"};
  }

  const lastHeartbeatAt = call?.lastHeartbeatAt;

  if (isValidTimestamp(lastHeartbeatAt)) {
    const ageMs = nowMs - lastHeartbeatAt.toMillis();

    if (ageMs <= HEARTBEAT_BUSY_MAX_MS) {
      return {busy: true, reason: "fresh_heartbeat", ageMs};
    }

    return {busy: false, reason: "stale_heartbeat", ageMs};
  }

  const anchor = getFallbackAgeAnchor(call);

  if (!anchor) {
    return {busy: false, reason: "no_age_anchor"};
  }

  const ageMs = nowMs - anchor.toMillis();

  if (ageMs <= MISSING_HEARTBEAT_GRACE_MS) {
    return {busy: true, reason: "missing_heartbeat_grace", ageMs};
  }

  return {busy: false, reason: "grace_expired", ageMs};
}

function logBusyEvaluation(callId, evaluation) {
  const {busy, reason, ageMs} = evaluation;
  const payload = {callId: callId || null, ageMs: ageMs ?? null};

  if (busy && reason === "fresh_heartbeat") {
    callDebugLog("[callBusy] busy true because fresh heartbeat", payload);
    return;
  }

  if (busy && reason === "missing_heartbeat_grace") {
    callDebugLog(
      "[callBusy] busy true because missing heartbeat but within grace",
      payload
    );
    return;
  }

  if (busy && reason === "active_call_route") {
    callDebugLog("[callBusy] busy true because active call route", payload);
    return;
  }

  if (!busy && reason === "stale_heartbeat") {
    callDebugLog("[callBusy] busy false because stale heartbeat", payload);
    return;
  }

  if (!busy && reason === "grace_expired") {
    callDebugLog(
      "[callBusy] busy false because missing heartbeat grace expired",
      payload
    );
    return;
  }

  if (!busy && reason === "no_age_anchor") {
    callDebugLog("[callBusy] busy false because no age anchor", payload);
  }
}

function CallListener() {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  const [incoming, setIncoming] = useState(null);
  const [caller, setCaller] = useState(null);
  const [isBusy, setIsBusy] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [joiningOverlay, setJoiningOverlay] = useState(null);

  const audioRef = useRef(null);
  const timeoutRef = useRef(null);
  const suspendedRef = useRef(false);
  const acceptInProgressRef = useRef(false);
  const zegoBootstrapRef = useRef(null);
  const lastBusyLogKeyRef = useRef("");

  const { showToast } = useLivoraToast();

  const functions = getFunctions(undefined, "us-central1");

  // ============================================================
  // Helpers
  // ============================================================
  const isMatchCall = (call) => String(call?.callType || "") === "match";

  const getRejectFn = (call) => {
    return isMatchCall(call)
      ? httpsCallable(functions, "rejectMatchedCall")
      : httpsCallable(functions, "rejectCall");
  };

  const getAcceptFn = (call) => {
    return isMatchCall(call)
      ? httpsCallable(functions, "acceptMatchedCall")
      : httpsCallable(functions, "acceptCall");
  };

  const stopRing = () => {
    try {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current = null;
      }
    } catch {}
  };

  const startRing = () => {
    try {
      stopRing();
      audioRef.current = new Audio("/sounds/ring.mp3");
      audioRef.current.loop = true;
      audioRef.current.play().catch(() => {});
    } catch {}
  };

  async function buildCallerData(userId) {
    const fallbackAvatar = "/images/default-avatar.png";

    try {
      const callerRef = doc(db, "publicProfiles", userId);
      const callerSnap = await getDoc(callerRef);

      if (!callerSnap.exists()) {
        return {
          photoURL: fallbackAvatar,
          profileImage: fallbackAvatar,
          avatar: fallbackAvatar,
          displayName: t("calls.unknown_user", { defaultValue: "User" }),
          name: t("calls.unknown_user", { defaultValue: "User" }),
        };
      }

      const callerData = callerSnap.data() || {};

      const rawAvatar =
        callerData.profileImage ||
        callerData.photoURL ||
        callerData.avatar ||
        callerData.image ||
        (Array.isArray(callerData.photos) && callerData.photos.length
          ? callerData.photos[0]
          : "") ||
        fallbackAvatar;

      const resolvedAvatar =
        rawAvatar === fallbackAvatar
          ? fallbackAvatar
          : (await resolveToDownloadUrl(rawAvatar)) || fallbackAvatar;

      return {
        id: callerSnap.id,
        ...callerData,
        photoURL: resolvedAvatar,
        profileImage: resolvedAvatar,
        avatar: resolvedAvatar,
      };
    } catch (e) {
      console.error("buildCallerData error:", e);
      return {
        photoURL: fallbackAvatar,
        profileImage: fallbackAvatar,
        avatar: fallbackAvatar,
        displayName: t("calls.unknown_user", { defaultValue: "User" }),
        name: t("calls.unknown_user", { defaultValue: "User" }),
      };
    }
  }

  // ============================================================
  // 👂 راقب هل المستخدم/ة في مكالمة ongoing (Busy) — READ ONLY
  // ============================================================
  useEffect(() => {
    if (!user?.uid) return;

    const activeQ = query(
      collection(db, "calls"),
      where("femaleUid", "==", user.uid),
      where("status", "==", "ongoing"),
      limit(1)
    );

    const unsub = onSnapshot(activeQ, (snap) => {
      const nowMs = Date.now();
      let busy = false;
      let evaluation = {busy: false, reason: "no_ongoing"};

      if (!snap.empty) {
        const docSnap = snap.docs[0];
        const call = docSnap.data() || {};
        const callId = docSnap.id;

        evaluation = evaluateOngoingBusy(call, nowMs);
        busy = evaluation.busy;

        const routeMatch = location.pathname === `/call/${callId}`;
        if (!busy && routeMatch) {
          busy = true;
          evaluation = {
            busy: true,
            reason: "active_call_route",
            ageMs: evaluation.ageMs ?? null,
          };
        }

        const logKey = `${callId}:${evaluation.busy}:${evaluation.reason}`;
        if (lastBusyLogKeyRef.current !== logKey) {
          lastBusyLogKeyRef.current = logKey;
          logBusyEvaluation(callId, evaluation);
        }
      } else {
        const logKey = "none:false:no_ongoing";
        if (lastBusyLogKeyRef.current !== logKey) {
          lastBusyLogKeyRef.current = logKey;
        }
      }

      setIsBusy(busy);

      if (busy) {
        setIncoming(null);
        setCaller(null);
        stopRing();
      } else {
        // ✅ مهم جدًا: بعد انتهاء المكالمة اسمح باستقبال مكالمة جديدة
        suspendedRef.current = false;
      }
    });

    return () => unsub();
  }, [user?.uid, location.pathname]);

  // ============================================================
  // 👂 الاستماع لآخر مكالمة ringing — READ ONLY
  // ============================================================
  useEffect(() => {
    if (!user?.uid) return;

    const ringingQ = query(
      collection(db, "calls"),
      where("femaleUid", "==", user.uid),
      where("status", "==", "ringing"),
      orderBy("createdAt", "desc"),
      limit(1)
    );

    const unsub = onSnapshot(ringingQ, async (snap) => {
      if (isBusy) return;

      // ✅ إذا اختفت مكالمة الرنين، فك التعليق
      if (snap.empty) {
        suspendedRef.current = false;
        setIncoming(null);
        setCaller(null);
        stopRing();
        return;
      }

      // ✅ أثناء الانتقال بعد القبول، تجاهل التحديثات مؤقتًا فقط
      if (suspendedRef.current) return;

      const d = snap.docs[0];
      const data = d.data();

      setIncoming({ id: d.id, ...data });
      logCallStartupEvent(d.id, "incoming_detected", "receiver");

      if (data.maleUid) {
        const preparedCaller = await buildCallerData(data.maleUid);
        setCaller(preparedCaller);
      } else {
        setCaller({
          photoURL: "/images/default-avatar.png",
          profileImage: "/images/default-avatar.png",
          avatar: "/images/default-avatar.png",
          displayName: t("calls.unknown_user", { defaultValue: "User" }),
          name: t("calls.unknown_user", { defaultValue: "User" }),
        });
      }
    });

    return () => unsub();
  }, [user?.uid, isBusy]);

  // ============================================================
  // 🔔 الرنين
  // ============================================================
  useEffect(() => {
    if (incoming && !isBusy) startRing();
    else stopRing();

    return () => stopRing();
  }, [incoming, isBusy]);

  useEffect(() => {
    if (!incoming?.id || !user?.uid) return;

    zegoBootstrapRef.current = null;
    const userName =
      user.displayName || user.name || user.username || "User";

    // Phase N2A: prepares Zego bootstrap + native Firebase auth in parallel
    // while this incoming call is still ringing (before Accept is tapped),
    // so Accept has nothing left to sequentially await.
    const entry = beginNativeCallPreparation({
      callId: incoming.id,
      uid: user.uid,
      userName,
      role: "receiver",
    });
    entry?.bootstrapPromise?.then((bootstrap) => {
      if (bootstrap?.token) {
        zegoBootstrapRef.current = bootstrap;
        console.log("[callPerf] token_prefetch_success", {
          callId: incoming.id,
        });
      }
    });
  }, [incoming?.id, user?.uid, user?.displayName, user?.name, user?.username]);

  // ============================================================
  // ⏳ رفض تلقائي (normal / match)
  // ============================================================
  useEffect(() => {
    if (!incoming || acceptInProgressRef.current) return;

    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(async () => {
      try {
        const rejectFn = getRejectFn(incoming);
        await rejectFn({
          callId: incoming.id,
          reason: isMatchCall(incoming) ? "no_answer_match" : "no_answer",
        });
      } catch (e) {
        console.error("auto reject error", e);
      }

      clearNativeCallPreparation(incoming.id);
      clearCallStartupTrace(incoming.id);
      suspendedRef.current = false;
      setIncoming(null);
      setCaller(null);
      stopRing();
    }, 30000);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [incoming]);

  // ============================================================
  // ❌ رفض يدوي
  // ============================================================
  const handleReject = async () => {
    if (!incoming?.id) return;
    if (accepting || acceptInProgressRef.current) return;

    try {
      callDebugLog("[callDebug] rejectCall called from", "CallListener:handleReject", {
        callId: incoming.id,
        currentUid: user?.uid,
      });
      const rejectFn = getRejectFn(incoming);
      await rejectFn({
        callId: incoming.id,
        reason: isMatchCall(incoming)
          ? "rejected_match_by_female"
          : "rejected_by_female",
      });
    } catch (e) {
      console.error("reject error", e);
    }

    clearNativeCallPreparation(incoming.id);
    clearCallStartupTrace(incoming.id);
    suspendedRef.current = false;
    setIncoming(null);
    setCaller(null);
    stopRing();
  };

  // ============================================================
  // ✅ قبول
  // ============================================================
  const resetAcceptState = () => {
    acceptInProgressRef.current = false;
    suspendedRef.current = false;
    setAccepting(false);
    setJoiningOverlay(null);
  };

  const handleAccept = async () => {
    if (!incoming?.id) return;
    if (accepting || acceptInProgressRef.current) return;

    const id = incoming.id;
    logCallStartupEvent(id, "accept_tapped", "receiver");
    const callSnapshot = incoming;
    const callerSnapshot = caller;
    const returnContext = buildReturnContextFromLocation(location);
    const returnTo = returnContext?.url || "/home";
    if (user?.uid && returnContext) {
      saveReturnContext(user.uid, id, returnContext);
      console.log(
        `[callReturn] captured role=receiver callId=${id} url=${returnContext.url}`
      );
    }

    acceptInProgressRef.current = true;
    suspendedRef.current = true;
    setAccepting(true);
    stopRing();

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    setJoiningOverlay({
      callId: id,
      caller: callerSnapshot,
      isMatch: isMatchCall(callSnapshot),
    });

    try {
      const acceptT0 = performance.now();
      console.log("[callPerf] acceptCall_start", { callId: id });
      const acceptFn = getAcceptFn(callSnapshot);
      const acceptRes = await acceptFn({ callId: id });
      console.log("[callPerf] acceptCall_success", {
        callId: id,
        elapsed: Math.round(performance.now() - acceptT0),
      });
      logCallStartupEvent(id, "accept_success", "receiver");

      if (acceptRes?.data?.ok === false && acceptRes?.data?.alreadyEnded) {
        throw new Error("call_already_ended");
      }

      const bootstrapReady = zegoBootstrapRef.current;

      const { target } = resolveCallRoomRoute({
        callId: id,
        role: "receiver",
        source: "call_listener_accept",
      });

      navigate(target, {
        replace: true,
        state: {
          returnTo,
          returnContext: returnContext || undefined,
          zegoBootstrap: bootstrapReady || undefined,
        },
      });

      setIncoming(null);
      setCaller(null);
      setJoiningOverlay(null);
      setAccepting(false);
      acceptInProgressRef.current = false;
    } catch (e) {
      console.error("accept error", e);
      // Phase N2A section 8: acceptCall failed - never join/publish, clear
      // any in-flight preparation for this callId rather than leaving it to
      // expire on its own TTL.
      clearNativeCallPreparation(id);
      clearCallStartupTrace(id);
      resetAcceptState();
      showToast(
        "error",
        t("calls.accept_failed", {
          defaultValue: "Could not join the call. Please try again.",
        })
      );
    }
  };

  if (joiningOverlay) {
    return (
      <IncomingCallCard
        incoming={{ id: joiningOverlay.callId }}
        caller={joiningOverlay.caller}
        isMatch={joiningOverlay.isMatch}
        accepting
        joining
        onAccept={() => {}}
        onReject={() => {}}
      />
    );
  }

  if (!incoming) return null;

  return (
    <IncomingCallCard
      incoming={incoming}
      caller={caller}
      isMatch={isMatchCall(incoming)}
      accepting={accepting}
      onAccept={handleAccept}
      onReject={handleReject}
    />
  );
}

export default CallListener;
